// Mount once at the root; then `openFileViewer(source)` from anywhere, or
// `useFileViewer().open(source)` from a component. The provider carries the
// app's translations, its optional loaders and any extra renderers, so no
// page passes them again.

import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { FileViewer } from './FileViewer.js';
import { closeFileViewer, fileViewerStore, openFileViewer, type OpenFileViewerInput } from './store.js';
import type { FileViewerLabels } from './labels.js';
import type { FileViewerLoaders, FileViewerOptions, FileViewerRenderer } from './types.js';

export interface FileViewerProviderProps {
  children: ReactNode;
  labels?: Partial<FileViewerLabels>;
  renderers?: FileViewerRenderer[];
  loaders?: FileViewerLoaders;
  /** Defaults every open inherits: size limits, which actions exist, telemetry. */
  defaults?: Partial<FileViewerOptions>;
  zIndex?: number;
  portalTarget?: HTMLElement | string;
}

export interface FileViewerApi {
  open: (input?: OpenFileViewerInput) => void;
  close: () => void;
}

const Ctx = createContext<FileViewerApi | null>(null);

export function FileViewerProvider({ children, labels, renderers, loaders, defaults, zIndex, portalTarget }: FileViewerProviderProps): ReactNode {
  const state = useSyncExternalStore(fileViewerStore.subscribe, fileViewerStore.getState, fileViewerStore.getState);
  const api = useMemo<FileViewerApi>(() => ({ open: openFileViewer, close: closeFileViewer }), []);

  return (
    <Ctx.Provider value={api}>
      {children}
      <FileViewer
        key={state.session}
        opened={state.opened}
        onClose={closeFileViewer}
        labels={labels}
        renderers={renderers}
        loaders={loaders}
        zIndex={zIndex}
        portalTarget={portalTarget}
        {...defaults}
        {...state.options}
      />
    </Ctx.Provider>
  );
}

/** Works outside the provider too: it falls through to the imperative API. */
export function useFileViewer(): FileViewerApi {
  return useContext(Ctx) ?? { open: openFileViewer, close: closeFileViewer };
}
